"use client";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { FetchButton } from "@/fetch-ui/FetchButton";
import { FetchInput } from "@/fetch-ui/FetchInput";
import { useUserStore } from "@/lib/stores/userStore";
import { Users } from "@/lib/users/types";
import { Loader2, Search, User } from "lucide-react";
import { usePathname, useSearchParams } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useEffect, useMemo, useState } from "react";
import { ImpersonateUserRow } from "./ImpersonateUserRow";

export const ImpersonateUserModal = ({
  getAllUsers,
  impersonateUser,
}: {
  getAllUsers: () => Promise<Users[]>;
  impersonateUser: (email: string, redirectTo?: string) => Promise<string>;
}) => {
  // Hooks
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const posthog = usePostHog();
  const shouldHideForAvaInbox = pathname?.startsWith("/ava/inbox");

  // State
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [users, setUsers] = useState<Users[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<Users | null>(null);
  const [isImpersonating, setIsImpersonating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Effects
  useEffect(() => {
    if (!isOpen) return;

    const fetchUsers = async () => {
      setIsLoading(true);
      try {
        const fetchedUsers = await getAllUsers();
        setUsers(fetchedUsers);
      } catch (error) {
        console.error("Error fetching users:", error);
        setUsers([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Handlers
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setSearchQuery("");
    }
  };

  const handleImpersonateClick = (user: Users) => {
    setSelectedUser(user);
    setShowConfirmation(true);
  };

  const handleConfirmImpersonate = async () => {
    if (!selectedUser) return;

    setIsImpersonating(true);
    setError(null);
    try {
      const searchParamsString = searchParams.toString();
      const redirectTo = searchParamsString ? `${pathname}?${searchParamsString}` : pathname;

      // Client side actions
      useUserStore.getState().clearUser();
      posthog?.reset();

      // Server side action
      const callbackUrl = await impersonateUser(selectedUser.email, redirectTo);

      // Client side navigation
      window.location.href = callbackUrl;
    } catch (error) {
      console.error("Error impersonating user:", error);
      setError("Failed to impersonate user");
      setIsImpersonating(false);
    }
  };

  const handleCancelImpersonate = () => {
    setShowConfirmation(false);
    setSelectedUser(null);
    setError(null);
  };

  // Helpers
  const getUserDisplayName = (user: Users | null): string => {
    if (!user) return "";
    if (user.full_name) return user.full_name;
    const nameParts = [user.first_name, user.last_name].filter(Boolean);
    if (nameParts.length > 0) return nameParts.join(" ");
    return user.email;
  };

  const filteredUsers = useMemo(() => {
    if (!searchQuery) return users;

    const lowerQuery = searchQuery.toLowerCase();
    return users.filter(
      (user) =>
        user.full_name?.toLowerCase().includes(lowerQuery) ||
        user.first_name?.toLowerCase().includes(lowerQuery) ||
        user.last_name?.toLowerCase().includes(lowerQuery) ||
        user.email.toLowerCase().includes(lowerQuery)
    );
  }, [users, searchQuery]);

  if (shouldHideForAvaInbox) {
    return null;
  }

  return (
    <>
      <div className="fixed bottom-24 right-4 z-50 rounded-full shadow-lg">
        <FetchButton
          onClick={() => setIsOpen(true)}
          variant="secondary"
          iconOnly
          leftIcon={<User className="w-5 h-5" />}
          tooltip="Impersonate user"
          className="!rounded-full"
        />
      </div>

      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Impersonate user</DialogTitle>
            <DialogDescription>Search for a user to impersonate their account</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <FetchInput
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              leftIcon={<Search className="w-4 h-4" />}
              autoFocus
            />

            <div className="h-[400px] overflow-y-auto space-y-2">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center h-full">
                  <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-sm font-medium">No users found</p>
                </div>
              ) : (
                filteredUsers.map((user) => (
                  <ImpersonateUserRow key={user.user_id} user={user} handleImpersonate={handleImpersonateClick} />
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm impersonation?</DialogTitle>
            <DialogDescription className="!mt-4">
              You will be logged out of your current account to impersonate {getUserDisplayName(selectedUser)}.
            </DialogDescription>
          </DialogHeader>
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-800 text-sm font-medium">{error}</p>
            </div>
          )}
          <DialogFooter>
            <FetchButton variant="secondary" size="sm" onClick={handleCancelImpersonate}>
              Cancel
            </FetchButton>
            <FetchButton variant="primary" size="sm" onClick={handleConfirmImpersonate} loading={isImpersonating}>
              Confirm
            </FetchButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
